// Publishing the viewer's review, and what a publish leaves behind (see
// pr/host.ts): the review with its inline drafts, then the conversation
// drafts, then the hand-off (attention changes and review requests), each
// recorded in `aftermath` (logic/aftermath.ts).
import type { WriteError } from '../../api/errors';
import { publishDrafts, type PublishResponse, type ReviewDraft } from '../../api/server';
import { IDLE_AFTERMATH, aftermath, blocksPublish, retrySteps, stepKey, type Aftermath, type AftermathEvent, type HandOffStep } from '../../logic/aftermath';
import { planSummary, publishRequest, type PublishPlan } from '../../logic/inbox';
import { dedup, published, subject } from '../optimistic';
import { draftSubjects, stepsOf, type AfterPublish, type Draft, type HandOffResult, type HandOffWrite, type PublishResult, type WriteHost } from './host';
import type { PrWrites } from './prWrites';

/** A publish from the Review view: what it hands off, and where the hand-off's run is told once it starts. */
interface PublishTrack {
  after: AfterPublish;
  started: (handOff: Promise<void>) => void;
}

export class Publishing {
  /** What the last publish left behind (see logic/aftermath): the verdict sent, a refusal, the hand-off's writes. */
  aftermath: Aftermath = $state(IDLE_AFTERMATH);
  /** The last publish's conversation failure, and the drafts it could not post. */
  heldUnposted: { error: WriteError; ids: readonly ReviewDraft['id'][] } | null = $state(null);
  /**
   * What of the viewer's pending review on GitHub the last publish could
   * not put back there (drafts mirroring its comments, its own summary),
   * and why, in the server's words: it is kept, and the next publish tries
   * again.
   */
  pendingUnrestored: { error: WriteError; ids: readonly ReviewDraft['id'][] } | null = $state(null);

  /** `sends` makes the hand-off's writes. */
  constructor(
    private readonly h: WriteHost,
    private readonly sends: Pick<PrWrites, 'changeAttention' | 'requestReviewer'>,
  ) {}

  after(e: AftermathEvent) {
    this.aftermath = aftermath(this.aftermath, e);
  }

  /**
   * Publish exactly `plan` (from `planFor`): the review with its inline
   * drafts, then the conversation drafts; the drafts not included stay.
   * Shown at once (drafts published, conversation drafts in the
   * conversation). The server refuses a plan whose drafts changed since,
   * and a failed review publishes nothing, keeping it all; once the review
   * is in, conversation drafts that fail to post are kept, with a failure
   * of their own whose Retry posts only them.
   */
  async publish(plan: PublishPlan, draft?: Draft): Promise<PublishResult> {
    const { handOff: _, ...res } = await this.publishWithHandOff(plan, draft, { attention: [], reviewers: [] });
    return res;
  }

  /**
   * Publish `plan` and, once it is in (the review submitted, or with no
   * review due, the conversation drafts posted), apply `after`: one write
   * per attention change and per review request, recorded one by one in
   * `aftermath`. Answers as soon as the publish is in; `handOff` settles
   * once the hand-off has. Refused, sending nothing, while another
   * publish or its hand-off is still being sent (`blocksPublish`).
   */
  async publishWithHandOff(plan: PublishPlan, draft: Draft | undefined, after: AfterPublish): Promise<HandOffResult> {
    let handOff: Promise<void> = Promise.resolve();
    if (blocksPublish(this.aftermath)) return { ok: false, reviewed: false, heldFailed: 0, writtenAt: null, handOff };
    const res = await this.sendPublish(plan, draft, { after, started: (p) => (handOff = p) });
    return { ...res, handOff };
  }

  /** Numbers the publishes that went in, so a hand-off write's late answer is told apart from the next one's. */
  private publishSerial = 0;

  /**
   * The publish write's answer, told to `aftermath` from inside the write,
   * so a Retry from the failure toast goes through it too: in (the review
   * submitted, or with none due, its conversation drafts posted) starts the
   * hand-off; anything else is refused.
   */
  landed(plan: PublishPlan, res: PublishResponse, track: PublishTrack) {
    if (!res.reviewed && (res.error || !plan.conversation.length)) {
      // Nothing went in. A conversation draft that could not be posted is told by its own note and Retry (`heldNote`).
      this.after({ kind: 'refused', error: null });
      return;
    }
    const serial = ++this.publishSerial;
    const steps = stepsOf(track.after);
    this.after({ kind: 'landed', serial, verdict: res.reviewed ? plan.verdict : null, writtenAt: res.written_at ?? null, steps });
    track.started(this.sendSteps(steps, serial));
  }

  /**
   * The Review view was left: the last publish's error and the finished
   * hand-off writes are forgotten, so coming back shows neither as
   * current. Failed ones stay, since their Retry is offered only there.
   */
  leftPublish() {
    this.after({ kind: 'left' });
  }

  /** Send again the hand-off writes that failed; false when none had, one failed again, or the hand-off is still being sent. */
  async retryHandOff(): Promise<boolean> {
    const steps = retrySteps(this.aftermath);
    if (!steps.length || this.aftermath.run.phase !== 'landed' || blocksPublish(this.aftermath)) return false;
    const serial = this.aftermath.run.serial;
    this.after({ kind: 'retry' });
    await this.sendSteps(steps, serial);
    return !steps.some((st) => retrySteps(this.aftermath).some((f) => stepKey(f) === stepKey(st)));
  }

  /**
   * Send publish `serial`'s hand-off writes, each on its own, recording its
   * result (the page tells a failure, with Retry): the review requests
   * first, then the attention changes, based at or after the last of them
   * (the aftermath's `basis`), so no event of the publish voids them.
   * While a request has failed the attention changes wait (`hold`): a
   * Retry sends the request first, and they go after it.
   */
  async sendSteps(steps: readonly HandOffStep[], serial: number): Promise<void> {
    const send = async (step: HandOffStep) => {
      let error = 'not saved';
      let at: string | undefined;
      const run = this.aftermath.run;
      const basis = run.phase === 'landed' && run.serial === serial ? run.basis : null;
      const how: HandOffWrite = { onError: (e) => (error = e.text), onTime: (t) => (at = t), unlisted: true, basis };
      const ok = step.kind === 'attention' ? await this.sends.changeAttention([step.change], how) : await this.sends.requestReviewer(step.login, how);
      this.after({ kind: 'step', serial, key: stepKey(step), result: ok ? { ok: true, at } : { ok: false, error } });
    };
    await Promise.all(steps.filter((st) => st.kind === 'review').map(send));
    const attention = steps.filter((st) => st.kind === 'attention');
    const run = this.aftermath.run;
    const requestFailed = run.phase === 'landed' && run.serial === serial && run.steps.some((x) => x.step.kind === 'review' && x.status.at === 'failed');
    if (requestFailed) return this.after({ kind: 'hold', serial, keys: attention.map(stepKey) });
    await Promise.all(attention.map(send));
  }

  /**
   * Post only the conversation drafts (Retry after some failed to post):
   * never submits a review. The same write as `publish` (`dedup.publish`):
   * refused while a publish is being sent, and retired when one starts.
   */
  async publishHeld(): Promise<PublishResult> {
    const plan = this.h.planFor('COMMENT', '');
    const res = await this.sendPublish({ ...plan, verdict: null, body: '', inline: [], resolve: [] });
    // Posted: a refusal of the publish they were part of is over.
    if (res.ok) this.after({ kind: 'cleared' });
    return res;
  }

  async sendPublish(plan: PublishPlan, draft?: Draft, track?: PublishTrack): Promise<PublishResult> {
    if (!plan.verdict && !plan.conversation.length) return { ok: true, reviewed: false, heldFailed: 0, writtenAt: null };
    // The drafts are published by the changelist server: not while server data is off.
    if (!this.h.serverData) return { ok: false, reviewed: false, heldFailed: plan.conversation.length, writtenAt: null };
    let answer: PublishResponse | null = null;
    const at = new Date().toISOString();
    const me = this.h.me();
    // The conversation drafts shown posted: the plan's until the server answers, then only those it posted.
    let posting: readonly ReviewDraft[] = plan.conversation;
    const ok = await this.h.write<PublishResponse>({
      kind: 'publish',
      dedup: dedup.publish,
      label: 'Publishing',
      done: (res) => planSummary(plan, res),
      // The header's publish button, and the drafts going out.
      subject: [subject.publish, ...[...plan.inline, ...plan.conversation].flatMap((d) => draftSubjects(d.id))],
      draft,
      apply: (s) => published({ ...plan, conversation: posting }, me, at)(s),
      send: async () => {
        if (track) {
          // A Retry from the toast too: never while the last publish's hand-off is being sent.
          if (blocksPublish(this.aftermath)) throw new Error('the last publish is still handing off; try again once it is done');
          this.after({ kind: 'publish' });
        }
        this.heldUnposted = null;
        this.pendingUnrestored = null;
        // Every attempt, a Retry from the failure toast included, tells what it kept and reads the drafts again.
        try {
          const res = await publishDrafts(this.h.owner, this.h.repo, this.h.number, publishRequest(plan));
          answer = res;
          posting = plan.conversation.filter((d) => res.published.includes(d.id));
          // Stores the server's drafts, so the overlay is laid again, with `posting` narrowed.
          this.h.dropDrafts(res.published);
          if (track) this.landed(plan, res, track);
          this.kept(res);
          this.unrestored(res);
          // The review is in: nothing after this may fail (and so roll back) the publish.
          if (res.reviewed) await this.h.flushMarks().catch((e) => console.warn('after publishing', e));
          return res;
        } finally {
          void this.h.loadDrafts();
        }
      },
      onError: track ? (error) => this.after({ kind: 'refused', error }) : undefined,
    });
    const res = answer as PublishResponse | null;
    if (!ok || !res) return { ok: false, reviewed: false, heldFailed: plan.conversation.length, writtenAt: null };
    return { ok: !res.error, reviewed: res.reviewed, heldFailed: res.kept.length, writtenAt: res.written_at ?? null };
  }

  /**
   * The server answered a publish: when it could not put some comments of
   * the viewer's pending review back on GitHub, say so and why
   * (`pendingUnrestored`, shown on the Review view). Nothing is lost: they
   * are drafts here, and the next publish puts them back.
   */
  private unrestored(res: PublishResponse) {
    const ids = res.pending_kept ?? [];
    // The server says what is not on GitHub, why, and that it is kept: its words are shown as they are.
    if (!res.pending_error) return;
    const text = res.pending_error;
    this.pendingUnrestored = { ids, error: { text: text.charAt(0).toUpperCase() + text.slice(1) + '.', link: null } };
  }

  /**
   * The server answered a publish: when it could not post some conversation
   * drafts, say why (`heldUnposted`, shown on the Review view) and list a
   * failure of their own whose Retry posts only them.
   */
  private kept(res: PublishResponse) {
    if (!res.error) return;
    const n = res.kept.length;
    const error: WriteError = {
      text: `${n} conversation comment${n === 1 ? '' : 's'} could not be posted (${res.error}) and ${n === 1 ? 'is' : 'are'} kept.`,
      link: null,
    };
    this.heldUnposted = { error, ids: res.kept };
    this.h.writes.failed({ kind: 'post-held', dedup: dedup.publish, label: 'Posting held comments', subject: subject.publish }, error, async () =>
      (await this.publishHeld()).ok,
    );
  }
}
